// src/pages/creator-panel/CallLogs/tabs/CallCostTab.js
// Cost-breakdown dashboard. Aggregates `session.usage[]` rows into the six
// category buckets, renders a Currency/Quantity-toggleable donut + per-
// category cards + auto-generated observations.
//
// Colors: derived from MUI theme semantic tokens (primary/secondary/info/
// success/warning/error) so the chart respects light/dark mode and never
// introduces a new palette. SVG donut is hand-rolled to avoid pulling in
// chart.js for ~6 segments.
import React, { useMemo, useState } from 'react';
import {
    Box, Card, CardContent, Typography, Checkbox,
    ToggleButtonGroup, ToggleButton, Stack, Grid, useTheme,
} from '@mui/material';
import {
    Mic as MicIcon,
    Memory as ModelIcon,
    VolumeUp as TtsIcon,
    SignalCellularAlt as SignalCellularAltIcon,
    Summarize as SummaryIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { formatDuration, formatCost } from '../utils/formatters';

// Category definitions. Order matches the spec; donut renders in this order.
// `themeKey` resolves to theme.palette[themeKey].main at render time.
const CATEGORY_DEFS = [
    { key: 'stt',                themeKey: 'info',     label: 'STT (Speech-to-Text)',     icon: <MicIcon fontSize="small" /> },
    { key: 'llm',                themeKey: 'success',  label: 'LLM',                       icon: <ModelIcon fontSize="small" /> },
    { key: 'tts',                themeKey: 'primary',  label: 'TTS (Text-to-Speech)',     icon: <TtsIcon fontSize="small" /> },
    { key: 'carrier',            themeKey: 'warning',  label: 'Carrier',                   icon: <SignalCellularAltIcon fontSize="small" /> },
    { key: 'analysis_summary',   themeKey: 'secondary', label: 'Analysis: Summary',         icon: <SummaryIcon fontSize="small" /> },
    { key: 'analysis_success',   themeKey: 'error',    label: 'Analysis: Success Evaluation', icon: <CheckIcon fontSize="small" /> },
];

// Aggregate usage[] rows into per-category numbers (cost + qty + details).
// Returns: { stt: {cost, qty, details}, llm: {...}, ... }
const aggregateCategories = (usage) => {
    const acc = {
        stt:                { cost: 0, qty: 0, details: { 'STT seconds': 0 } },
        // The four audio/text rows only carry values for Gemini Live calls;
        // zero-valued details are hidden by the card, so classic calls are
        // visually unchanged.
        llm:                { cost: 0, qty: 0, details: {
            'Prompt tokens': 0, 'Completion tokens': 0,
            'Input audio tokens': 0, 'Input text tokens': 0,
            'Output audio tokens': 0, 'Output text tokens': 0,
        } },
        tts:                { cost: 0, qty: 0, details: { 'Characters': 0 } },
        carrier:            { cost: 0, qty: 0, details: { 'Seconds': 0 } },
        analysis_summary:   { cost: 0, qty: 0, details: { 'Prompt tokens': 0, 'Completion tokens': 0 } },
        analysis_success:   { cost: 0, qty: 0, details: { 'Prompt tokens': 0, 'Completion tokens': 0 } },
    };

    for (const u of (usage || [])) {
        const b = u.cost_breakdown || {};
        const purpose = String(b.purpose || '').toLowerCase();
        const isAnalysis = purpose.includes('analysis');

        // STT
        acc.stt.cost += Number(b.stt_cost_usd || 0);
        acc.stt.qty  += Number(b.stt_seconds  || 0);
        acc.stt.details['STT seconds'] += Number(b.stt_seconds || 0);

        // TTS
        acc.tts.cost += Number(b.tts_cost_usd || 0);
        acc.tts.qty  += Number(b.tts_characters || 0);
        acc.tts.details['Characters'] += Number(b.tts_characters || 0);

        // Carrier
        acc.carrier.cost += Number(b.carrier_cost_usd || 0);
        acc.carrier.qty  += Number(b.carrier_seconds  || 0);
        acc.carrier.details['Seconds'] += Number(b.carrier_seconds || 0);

        // LLM split — analysis rows count under their own category buckets;
        // everything else (turn rows) goes to LLM. The spec's two analysis
        // categories (Summary + Success Eval) come from a single Gemini
        // call in our system; we mirror the spec layout but keep them
        // honest by attributing the same row to "Summary" only and leaving
        // "Success Evaluation" as zero — the success_eval IS produced by
        // the same call, no additional tokens spent.
        const inTok  = Number(b.llm_input_tokens  || 0);
        const outTok = Number(b.llm_output_tokens || 0);
        const llmCost = Number(b.llm_cost_usd || 0);
        if (isAnalysis) {
            acc.analysis_summary.cost += llmCost;
            acc.analysis_summary.qty  += inTok + outTok;
            acc.analysis_summary.details['Prompt tokens']     += inTok;
            acc.analysis_summary.details['Completion tokens'] += outTok;
        } else {
            acc.llm.cost += llmCost;
            acc.llm.qty  += inTok + outTok;
            acc.llm.details['Prompt tokens']     += inTok;
            acc.llm.details['Completion tokens'] += outTok;
            // Gemini Live turns carry a per-modality split (audio vs text bill
            // at different rates) — absent on classic Gemini calls.
            acc.llm.details['Input audio tokens']  += Number(b.llm_input_audio_tokens  || 0);
            acc.llm.details['Input text tokens']   += Number(b.llm_input_text_tokens   || 0);
            acc.llm.details['Output audio tokens'] += Number(b.llm_output_audio_tokens || 0);
            acc.llm.details['Output text tokens']  += Number(b.llm_output_text_tokens  || 0);
        }
    }
    return acc;
};

const pctOf = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtSec = (n) => `${Number(n || 0).toFixed(1)}s`;

// Donut chart — SVG, no library. Renders one arc per visible category.
const DonutChart = ({ slices, total, totalLabel }) => {
    const size = 220;
    const stroke = 28;
    const radius = (size - stroke) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * radius;

    let offset = 0;
    const total_ = slices.reduce((s, x) => s + x.value, 0) || 1;

    return (
        <Box sx={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {/* Background ring — uses theme divider color */}
                <circle cx={cx} cy={cy} r={radius} fill="none" stroke="currentColor"
                    strokeWidth={stroke} style={{ color: 'rgba(148,163,184,0.2)' }} />
                {slices.map((s) => {
                    const frac = s.value / total_;
                    const dash = frac * circumference;
                    const arc = (
                        <circle
                            key={s.key}
                            cx={cx} cy={cy} r={radius}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={stroke}
                            strokeDasharray={`${dash} ${circumference - dash}`}
                            strokeDashoffset={-offset}
                            transform={`rotate(-90 ${cx} ${cy})`}
                        />
                    );
                    offset += dash;
                    return arc;
                })}
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{totalLabel}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total Cost</Typography>
            </Box>
        </Box>
    );
};

const CategoryCard = ({ def, color, cost, qty, details, pct, mode }) => {
    const isCurrency = mode === 'currency';
    const headerRight = isCurrency
        ? formatCost(cost)
        : qty > 0
            ? (def.key === 'tts' ? `${fmtNum(qty)} chars`
              : def.key === 'stt' || def.key === 'carrier' ? fmtSec(qty)
              : `${fmtNum(qty)} tokens`)
            : '0';

    return (
        <Card variant="outlined">
            <CardContent sx={{ pb: '12px !important' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ color, display: 'flex' }}>{def.icon}</Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{def.label}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{headerRight}</Typography>
                </Stack>

                <Box sx={{
                    height: 4, borderRadius: 2, bgcolor: 'action.hover',
                    overflow: 'hidden', mb: 0.5,
                }}>
                    <Box sx={{
                        height: '100%',
                        width: `${Math.min(100, pct)}%`,
                        bgcolor: color,
                        transition: 'width 200ms ease',
                    }} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                    {pct.toFixed(1)}% of total
                </Typography>

                {Object.keys(details || {}).length === 0 || Object.values(details).every(v => !v) ? (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        No additional details available.
                    </Typography>
                ) : (
                    <Box>
                        {Object.entries(details).map(([k, v]) => (
                            (Number(v) > 0) && (
                                <Typography key={k} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                    {k}: {typeof v === 'number' && k.toLowerCase().includes('seconds') ? fmtSec(v) : fmtNum(v)}
                                </Typography>
                            )
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const CallCostTab = ({ session }) => {
    const theme = useTheme();
    const [mode, setMode] = useState('currency'); // 'currency' | 'quantity'
    const [visibleKeys, setVisibleKeys] = useState(() => new Set(CATEGORY_DEFS.map(c => c.key)));

    // Resolve category colors against the live theme so dark mode works.
    const palette = useMemo(() => {
        const out = {};
        for (const c of CATEGORY_DEFS) {
            out[c.key] = theme.palette[c.themeKey]?.main || theme.palette.text.primary;
        }
        return out;
    }, [theme]);

    const aggregated = useMemo(() => aggregateCategories(session?.usage), [session?.usage]);

    // Total Cost in the summary card always reflects the TRUE total
    // (regardless of which legend boxes are checked).
    const trueTotalCost = useMemo(
        () => Object.values(aggregated).reduce((s, x) => s + (x?.cost || 0), 0),
        [aggregated],
    );

    // Donut + per-card percentages reflect VISIBLE categories only —
    // matches the spec's "uncheck to remove segment, percentages recompute".
    const visibleTotalCost = useMemo(() => {
        return CATEGORY_DEFS.reduce(
            (s, def) => visibleKeys.has(def.key) ? s + (aggregated[def.key]?.cost || 0) : s,
            0,
        );
    }, [aggregated, visibleKeys]);

    const slices = useMemo(() => {
        return CATEGORY_DEFS
            .filter(def => visibleKeys.has(def.key))
            .map(def => ({
                key: def.key,
                color: palette[def.key],
                value: aggregated[def.key]?.cost || 0,
            }))
            .filter(s => s.value > 0);
    }, [aggregated, visibleKeys, palette]);

    const observations = useMemo(() => {
        const items = CATEGORY_DEFS
            .filter(def => visibleKeys.has(def.key))
            .map(def => ({
                label: def.label,
                cost: aggregated[def.key]?.cost || 0,
                pct: pctOf(aggregated[def.key]?.cost || 0, visibleTotalCost),
            }))
            .filter(x => x.cost > 0)
            .sort((a, b) => b.cost - a.cost)
            .slice(0, 3);
        return items;
    }, [aggregated, visibleKeys, visibleTotalCost]);

    const toggleVisible = (key) => {
        setVisibleKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    return (
        <Box>
            {/* Section header + Currency/Quantity toggle */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Cost Breakdown</Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mode}
                    onChange={(_, v) => v && setMode(v)}
                    color="primary"
                >
                    <ToggleButton value="currency">Currency</ToggleButton>
                    <ToggleButton value="quantity">Quantity</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {/* Top stat cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total Cost</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{formatCost(trueTotalCost)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Duration</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{formatDuration(session?.duration_seconds || 0)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Categories</Typography>
                            <Stack direction="row" alignItems="baseline" spacing={1}>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>{CATEGORY_DEFS.length}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {visibleKeys.size} visible
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Donut + legend + observations */}
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-start">
                <Grid item xs={12} md={5}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <DonutChart
                            slices={slices}
                            total={visibleTotalCost}
                            totalLabel={mode === 'currency' ? formatCost(visibleTotalCost) : fmtNum(slices.reduce((s, x) => s + x.value, 0))}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                    {/* Legend */}
                    <Box sx={{ mb: 2 }}>
                        {CATEGORY_DEFS.map(def => {
                            const row = aggregated[def.key] || { cost: 0 };
                            const pct = pctOf(row.cost, visibleTotalCost);
                            const checked = visibleKeys.has(def.key);
                            return (
                                <Stack
                                    key={def.key}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={() => toggleVisible(def.key)}
                                        sx={{ p: 0.5 }}
                                    />
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: palette[def.key] }} />
                                    <Typography variant="body2" sx={{ flex: 1, opacity: checked ? 1 : 0.5 }}>
                                        {def.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {mode === 'currency' ? formatCost(row.cost) : fmtNum(row.qty || 0)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 48, textAlign: 'right' }}>
                                        {pct.toFixed(1)}%
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Box>

                    {/* Observations */}
                    {observations.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Observations</Typography>
                            {observations.map(o => (
                                <Typography key={o.label} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    {o.label} contributes {o.pct.toFixed(1)}% ({formatCost(o.cost)})
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Grid>
            </Grid>

            {/* Per-category detail cards */}
            <Grid container spacing={2}>
                {CATEGORY_DEFS.map(def => {
                    const row = aggregated[def.key] || { cost: 0, qty: 0, details: {} };
                    const pct = pctOf(row.cost, visibleTotalCost);
                    return (
                        <Grid key={def.key} item xs={12} sm={6} md={4}>
                            <CategoryCard
                                def={def}
                                color={palette[def.key]}
                                cost={row.cost}
                                qty={row.qty}
                                details={row.details}
                                pct={pct}
                                mode={mode}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default CallCostTab;
